import {Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {HttpParams} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {AuthService} from '../../../services/auth/auth.service';
import {NotificationService} from '../../../services/common/notification.service';
import {FormOptionsLists} from '../../../shared/FormOptionsLists';
import {SearchTriggerObject} from '../../../shared/SearchTrigger';
import {CategoryTreeComponent, EventData} from '../../common/category-tree/category-tree.component';
import {TableComponent} from '../../common/table/table.component';
import {GlobalCanDoStatementService} from '../../../services/can-do-statement/global-can-do-statement.service';
import {DetailedCanDoStatementService} from '../../../services/can-do-statement/detailed-can-do-statement.service';
import {GlobalCanDoStatementCategoryService} from '../../../services/can-do-statement/global-can-do-statement-category.service';
import {DetailedCanDoStatementCategoryService} from '../../../services/can-do-statement/detailed-can-do-statement-category.service';
import {NewCanDoStatementComponent} from '../create/new-can-do-statement/new-can-do-statement.component';
import {NewCanDoStatementCategoryComponent} from '../create/new-category/new-can-do-statement-category.component';

export type CdsFlavor = 'global' | 'detailed';

@Component({
  selector: 'app-can-do-statement-panel',
  templateUrl: './can-do-statement-panel.component.html',
  styleUrls: ['./can-do-statement-panel.component.scss'],
  standalone: false
})
export class CanDoStatementPanelComponent implements OnInit, OnChanges {

  @Input() flavor: CdsFlavor = 'global';
  @Input() entryId: number = null;
  @Input() categoryId: number = null;
  @Input() initialQuery: string = null;

  @ViewChild(TableComponent) tableC: TableComponent;
  @ViewChild(CategoryTreeComponent) treeC: CategoryTreeComponent;

  entryService: GlobalCanDoStatementService | DetailedCanDoStatementService;
  categoryService: GlobalCanDoStatementCategoryService | DetailedCanDoStatementCategoryService;

  title: string;
  categoryTreeTitle: string;
  displayedColumns: string[];
  formOptionsLists = new FormOptionsLists();

  categoryControl: number = null;
  reloadRootsTrigger = false;
  searchTrigger: SearchTriggerObject = {trigger: false, params: new HttpParams()};
  resetCategoryControl = false;
  resetSearchTrigger = false;

  isAuthorized: boolean;
  isAdmin: boolean;
  displayEditingTools = false;

  // checkboxes to add entries to a Sammelmappe, off until the user asks for them
  selectMode = false;

  toggleSelectMode(): void {
    this.selectMode = !this.selectMode;
  }

  constructor(
    private globalService: GlobalCanDoStatementService,
    private detailedService: DetailedCanDoStatementService,
    private globalCategoryService: GlobalCanDoStatementCategoryService,
    private detailedCategoryService: DetailedCanDoStatementCategoryService,
    private notificationService: NotificationService,
    public authService: AuthService,
    public matDialog: MatDialog
  ) {}

  ngOnInit() {
    this.entryService = this.flavor === 'detailed' ? this.detailedService : this.globalService;
    this.categoryService = this.flavor === 'detailed' ? this.detailedCategoryService : this.globalCategoryService;
    this.title = this.flavor === 'detailed' ? 'Detaillierte Kannbeschreibungen' : 'Globale Kannbeschreibungen';
    this.categoryTreeTitle = this.flavor === 'detailed' ? 'Detaillierte Fertigkeiten' : 'Globale Fertigkeiten';

    [this.isAuthorized, this.isAdmin] = this.authService.isAuthorizedAndAdmin();
    this.reloadRootsTrigger = !this.reloadRootsTrigger;
    this.displayedColumns = this.isAdmin && this.displayEditingTools
      ? this.formOptionsLists.displayedColumnsCdsEditing
      : this.formOptionsLists.displayedColumnsCdsDetails;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['entryId'] && this.entryId) {
      // clear any previously selected category so it doesn't hide this deep-linked entry
      this.resetCategoryControl = !this.resetCategoryControl;
      this.categoryControl = null;
      let params = new HttpParams();
      params = params.set('id', String(this.entryId));
      this.handleSearch(params);
    }
    // On the initial load the tree reads [categoryId] during the root load that
    // ngOnInit kicks off, so only later changes (deep-linking to another Thema
    // while already on this tab) need to re-trigger it. The tree consumes
    // categoryId when the roots (re)load, so toggle reloadRootsTrigger.
    if (changes['categoryId'] && !changes['categoryId'].firstChange && this.categoryId) {
      this.reloadRootsTrigger = !this.reloadRootsTrigger;
      // clear the rest of the filter so it doesn't combine with the deep-linked category
      this.resetSearchTrigger = !this.resetSearchTrigger;
      if (this.tableC) {
        this.tableC.sort.active = 'created';
        this.tableC.paginator.pageSize = 25;
        this.tableC.paginator.pageIndex = 0;
      }
    }
  }

  toggleEditingTools(change: boolean) {
    if (change) {
      this.displayEditingTools = !this.displayEditingTools;
    }
    if (this.displayEditingTools && this.isAdmin) {
      if (!this.displayedColumns.includes('edit')) {
        const lastIndex = this.displayedColumns.length - 1;
        this.displayedColumns.splice(lastIndex, 0, 'edit');
      }
    } else {
      this.displayedColumns = this.displayedColumns.filter(c => c !== 'edit');
    }
  }

  openNewCategoryDialog() {
    const dialogRef = this.matDialog.open(NewCanDoStatementCategoryComponent, {
      data: {service: this.categoryService}
    });
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result) {
          this.reloadRootsTrigger = !this.reloadRootsTrigger;
        }
      },
      error: (error) => this.notificationService.openSnackBar(error)
    });
  }

  openNewEntryDialog() {
    const dialogRef = this.matDialog.open(NewCanDoStatementComponent, {
      data: {service: this.entryService}
    });
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result) {
          this.searchTrigger.params = new HttpParams();
        }
      },
      error: (error) => this.notificationService.openSnackBar(error)
    });
  }

  handleChildSearch(data: EventData) {
    this.categoryControl = data.categoryControl;
    if (data.resetPageIndex && this.tableC) {
      this.tableC.paginator.pageIndex = 0;
    }
    // clear any previously deep-linked/searched entry id so it doesn't stay combined with the new category
    this.resetSearchTrigger = !this.resetSearchTrigger;
    this.searchTrigger.trigger = !this.searchTrigger.trigger;
  }

  handleSearch(params: HttpParams) {
    this.searchTrigger.params = params;
  }

  handleResetFilter() {
    this.tableC.sort.active = 'created';
    this.tableC.paginator.pageSize = 25;
    this.tableC.paginator.pageIndex = 0;
    this.resetCategoryControl = !this.resetCategoryControl;
    this.categoryControl = null;
  }
}
